

precision highp float;
precision highp sampler2D;

#define M_PI 3.14159265358

uniform float tileScale;
uniform vec2 tilePosition;
uniform vec2 tex0xy;
uniform vec2 tex1xy;
uniform float tex0size;
uniform float tex1size;
uniform bool useAlphaAsMask;
uniform sampler2D rttTexture;
uniform vec2 rttXY;
uniform float rttScale;
uniform float time;
uniform sampler2D tex0;
uniform sampler2D tex1;
uniform float timestep;
uniform float canvasDiagonal;
uniform float angleDirectionShiftSkip;
uniform bool uniformSpeed;
uniform float tileGridPass;

in vec2 rttCoord;
out vec4 fragColor;

float modI(float a,float b) {
    float m=a-floor((a+0.5)/b)*b;
    return floor(m+0.5);
}


void main() {
  vec4 color = texture(rttTexture, rttCoord);

  // Not moving this particle because it has been moved as part of the same tile grid pass already
  // (the odd or even value is used a s flag to mark the ones being processed already)
  if (tileGridPass == 0. && modI(floor(color.r * 255.), 2.) == 0.) {
    fragColor = color;
    return;
  }

  // Not moving this particle because it has been moved as part of the same tile grid pass already
  // (the odd or even value is used a s flag to mark the ones being processed already)
  if (tileGridPass == 1. && modI(floor(color.r * 255.), 2.) != 0.) {
    fragColor = color;
    return;
  }

  vec2 pos = color.ba + color.rg / 255.0;

  vec2 posInTile = (rttXY + rttScale * pos) / tileScale - tilePosition;

  // the value 4000 is a somwhat arbitrary diagonal size (in number of pixel)
  // for which the particle speed is comparable with or without `uniformSpeed`
  // on a retina screen.
  float uniformSpeedFactor = uniformSpeed ? (4000. / canvasDiagonal) : 1.;

  if (posInTile.x >= 0. && posInTile.x <= 1. &&
      posInTile.y >= 0. && posInTile.y <= 1.) {

    posInTile.y = 1.0 - posInTile.y;

    vec2 tex0coord = tex0xy + posInTile * tex0size;
    vec2 tex1coord = tex1xy + posInTile * tex1size;

    vec4 tex0Color = texture(tex0, tex0coord);
    vec4 tex1Color = texture(tex1, tex1coord);

    // We use the alpha chan to flag no-data pixels.
    // In this case, we return pos at it was received so that the speed is 0
    if (useAlphaAsMask && (tex0Color.a < 1. || tex1Color.a < 1.)) {
      fragColor = color;
      return;
    }

    vec2 uv;

    bool skipRendering = false;

    if (D_WAVES) {
      vec2 value0 = tex0Color.D_CHANNELS;
      vec2 value1 = tex1Color.D_CHANNELS;

      // https://apps.ecmwf.int/codes/grib/param-db?id=140230
      // Zero means 'coming from the north' and 90 'coming from the east'.
      float dir0 = 2.0 * M_PI * (-value0.x - 0.25);
      float dir1 = 2.0 * M_PI * (-value1.x - 0.25);

      // sqrt makes it look much nicer
      float speed = sqrt(1.0 / mix(value0.y, value1.y, time));

      uv = vec2(
        mix(cos(dir0), cos(dir1), time),
        mix(sin(dir0), sin(dir1), time)
      ) * (D_MIN + (D_MAX - D_MIN) * speed);
    } else {
      vec4 lookup = mix(tex0Color, tex1Color, time);
      vec2 value = lookup.D_CHANNELS;
      uv = D_MIN + (D_MAX - D_MIN) * value;

      if (angleDirectionShiftSkip < 89.999) {
        // From here, we are computing the uv at the next position
        // so that we can check if the angle is very different
        vec2 offset = vec2(uv.x, -uv.y) * timestep * uniformSpeedFactor;
        vec2 nextPos = pos + offset;

        vec2 posNextInTile = (rttXY + rttScale * nextPos) / tileScale - tilePosition;
        posNextInTile.y = 1.0 - posNextInTile.y;

        vec2 tex0coordNext = tex0xy + posNextInTile * tex0size;
        vec2 tex1coordNext = tex1xy + posNextInTile * tex1size;

        vec4 tex0ColorNext = texture(tex0, tex0coordNext);
        vec4 tex1ColorNext = texture(tex1, tex1coordNext);

        vec4 lookupNext = mix(tex0ColorNext, tex1ColorNext, time);
        vec2 valueNext = lookupNext.D_CHANNELS;
        vec2 uvNext = D_MIN + (D_MAX - D_MIN) * valueNext;

        // the rendering should be skipped if the angle between uv and uvNext is wider than a certain angle in degree
        skipRendering = dot(normalize(uv), normalize(uvNext)) < cos( angleDirectionShiftSkip * M_PI / 180.);
      }
    }

    vec2 offset = vec2(uv.x, -uv.y) * timestep * uniformSpeedFactor;


    if (skipRendering) {
      offset = vec2(0., 0.);
    }

    vec2 newPos = pos + offset;

    fragColor = vec4(
        fract(newPos * 255.0),
        floor(newPos * 255.0) / 255.0);

    float red255 = floor(fragColor.r * 255.);

    // To add a flag on whether this position was already moved, we are going to force fragColor.r to
    // be an odd or an even number depending on the uniform tileGridPass.
    // Basically, if tileGridPass is even, then the red component of the output should be even
    // if tileGridPass is odd, then red component should be odd as well

    // If tileGridPass is even
    if (tileGridPass == 0.) {

      // if red255 is odd we need to turn it even
      if (modI(red255, 2.) != 0.) {
        red255 -= 1.;
      }
    } 
    
    // If tileGridPass is odd
    else {
      // if red255 is even we need to turn it odd
      if (modI(red255, 2.) == 0.) {
        red255 += 1.;
      }
    }

    fragColor.r = red255 / 255.;

  } else {
    // retain the old color to avoid numerical issues on iOS
    fragColor = color;
  }
}
